import { Flex, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';

import { FEELING_ICONS } from '../../../utils/feelingIcons';
import { isEffectsDisabledState } from '../../../utils/isEffectsDisabledState';
import type { WizardProps, WizardStepProps } from '../../../utils/wizard/types';
import { WizardFeeling, WizardStepKey } from '../../../utils/wizard/types';

import tileStyles from '../wizardOptionTile.module.css';
import stepStyles from '../wizardStep.module.css';
import styles from './feelingsStep.module.css';

interface FeelingsStepProps extends WizardStepProps {
  disabledFeelingTags?: string[];
  showRelievedFeelingLabel?: boolean;
}

const FEELINGS: { label: string; value: WizardFeeling }[] = [
  {
    label: 'Relaxed',
    value: WizardFeeling.Relaxed,
  },
  {
    label: 'Sleepy',
    value: WizardFeeling.Sleepy,
  },
  {
    label: 'Hungry',
    value: WizardFeeling.Hungry,
  },
  {
    label: 'Blissful',
    value: WizardFeeling.Blissful,
  },
  {
    label: 'Pain free',
    value: WizardFeeling.PainFree,
  },
  {
    label: 'Energetic',
    value: WizardFeeling.Energetic,
  },
  {
    label: 'Creative',
    value: WizardFeeling.Creative,
  },
  {
    label: 'Non-impairing',
    value: WizardFeeling.NotHigh,
  },
];

const ALL_FEELING_VALUES = FEELINGS.map((f) => f.value);
const MAX_SELECTION = 3;

export function feelingsStepEnabledForStore({
  disabledFeelingTags,
  storeState,
}: WizardProps): boolean {
  if (isEffectsDisabledState(storeState)) return false;
  return !ALL_FEELING_VALUES.every((f) => disabledFeelingTags.includes(f));
}

interface FeelingOptionProps {
  disabled: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  selected: boolean;
}

function FeelingOption({
  disabled,
  icon,
  label,
  onClick,
  selected,
}: FeelingOptionProps) {
  const className = clsx(
    tileStyles['tile'],
    stepStyles['option'],
    styles['option'],
    selected && tileStyles['selected'],
    disabled && tileStyles['disabled']
  );

  return (
    <button
      type="button"
      className={className}
      onClick={disabled ? undefined : onClick}
      aria-pressed={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
    >
      {icon}
      <Typography variant="body-semibold">{label}</Typography>
    </button>
  );
}

export function FeelingsStep({
  disabledFeelingTags,
  form,
  showRelievedFeelingLabel,
  updateForm,
}: FeelingsStepProps) {
  const selections = form[WizardStepKey.Feelings];
  const isMaxed = selections.length >= MAX_SELECTION;
  const visibleFeelings = FEELINGS.filter(
    ({ value }) => !disabledFeelingTags?.includes(value)
  ).map((feeling) =>
    showRelievedFeelingLabel && feeling.value === WizardFeeling.PainFree
      ? { ...feeling, label: 'Relieved' }
      : feeling
  );

  const handleToggle = (value: string) => {
    const isSelected = selections.includes(value);

    if (isSelected) {
      updateForm({
        ...form,
        [WizardStepKey.Feelings]: selections.filter((v) => v !== value),
      });
    } else if (!isMaxed) {
      updateForm({
        ...form,
        [WizardStepKey.Feelings]: [...selections, value],
      });
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center" gap={32} width="100%">
      <Flex flexDirection="column" alignItems="center" gap={8}>
        <Typography variant="h2-semibold" textAlign="center">
          How do you want to feel?
        </Typography>
        <Typography textAlign="center">Pick up to 3 effects.</Typography>
      </Flex>

      <div className={clsx(stepStyles['grid'], styles['grid'])}>
        {visibleFeelings.map(({ label, value }) => {
          const selected = selections.includes(value);
          const disabled = !selected && isMaxed;
          const Icon = FEELING_ICONS[value];

          return (
            <FeelingOption
              key={value}
              disabled={disabled}
              icon={Icon && <Icon size="md" />}
              label={label}
              onClick={() => handleToggle(value)}
              selected={selected}
            />
          );
        })}
      </div>
    </Flex>
  );
}
